<div class="container py-4">

  <div class="page-header">
    <h1>{{ isEditMode ? 'Edit Project' : 'New Project' }}</h1>
    <div *ngIf="isEditMode" class="page-header__actions">
      <button class="pw-btn pw-btn--ghost" routerLink="/main/project/{{ projectId }}/analytics"
        type="button">
        Analytics
      </button>
      <button class="pw-btn pw-btn--ghost" routerLink="/main/project/{{ projectId }}/notifications"
        type="button">
        Notifications
      </button>
      <button class="pw-btn pw-btn--ghost" routerLink="/main/project/{{ projectId }}/api-keys"
        type="button">
        API Keys
      </button>
      <button class="pw-btn pw-btn--danger-outline" (click)="showDeleteModal = true" type="button">
        Delete Project
      </button>
    </div>
  </div>

  <form>
    <!-- Project name -->
    <div class="form-group mb-4">
      <label for="projectName">Project Name</label>
      <input type="text" class="form-control" id="projectName" placeholder="Project X"
        [(ngModel)]="project.name" [ngModelOptions]="{standalone: true}">
    </div>

    <!-- Teams (create only): who may see the new project -->
    @if (!isEditMode && (teams.length > 1 || (seesAllProjects && teams.length))) {
      <div class="form-group mb-4" data-testid="project-teams">
        <label>Teams</label>
        <div class="team-pick">
          @for (team of teams; track team.uuid) {
            <label class="team-pick__option" [class.is-on]="selectedTeams.has(team.uuid)">
              <input type="checkbox" [checked]="selectedTeams.has(team.uuid)" (change)="toggleTeam(team.uuid)"
                data-testid="project-team-option">
              {{ team.name }}
            </label>
          }
        </div>
        <small class="text-muted d-block mt-2">
          @if (seesAllProjects) {
            Members of these teams will see the project. Without a team, only the owner and admins see it.
          } @else {
            Choose the team this project belongs to. Its members will see the project.
          }
        </small>
        @if (teamError) {
          <small class="text-danger d-block" data-testid="project-team-error">{{ teamError }}</small>
        }
      </div>
    } @else if (!isEditMode && teamError) {
      <p class="text-danger" data-testid="project-team-error">{{ teamError }}</p>
    }

    <!-- Teams (edit): read-only, the owner changes them on the Teams page -->
    @if (isEditMode && project.teams) {
      <div class="form-group mb-4" data-testid="project-teams-readonly">
        <label>Teams</label>
        <div class="team-pick">
          @for (team of project.teams; track team.uuid) {
            <span class="team-pick__option is-on">{{ team.name }}</span>
          } @empty {
            <span class="text-muted">Not in any team — only the owner and admins see it.</span>
          }
        </div>
        <small class="text-muted d-block mt-2">
          The owner changes a project’s teams on <a routerLink="/main/organization/teams">Organization → Teams</a>.
        </small>
      </div>
    }

    <!-- Logo (edit only) -->
    <div *ngIf="isEditMode" class="form-group mb-4">
      <label>Logo</label>
      <div class="logo-field" [class.logo-field--dragover]="logoDragOver" data-testid="project-logo-dropzone"
        (dragenter)="onLogoDragOver($event)" (dragover)="onLogoDragOver($event)"
        (dragleave)="onLogoDragLeave($event)" (drop)="onLogoDrop($event)">
        <div class="logo-field__preview" [class.logo-field__preview--empty]="!project.logo_url">
          <img *ngIf="project.logo_url" [src]="logoSrc" alt="Project logo" data-testid="project-logo-preview">
          <span *ngIf="!project.logo_url">No logo</span>
        </div>
        <div class="logo-field__body">
          <div class="d-flex flex-wrap gap-2">
            <label class="pw-btn pw-btn--ghost logo-field__upload" [class.logo-field__upload--busy]="logoBusy">
              {{ logoBusy ? 'Uploading…' : (project.logo_url ? 'Replace logo' : 'Upload logo') }}
              <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden
                [disabled]="logoBusy" (change)="onLogoSelected($event)" data-testid="project-logo-input">
            </label>
            <button *ngIf="project.logo_url" type="button" class="pw-btn pw-btn--danger-outline"
              [disabled]="logoBusy" (click)="removeLogo()">Remove</button>
          </div>
          <small class="text-muted d-block mt-2">
            Drop an image here or upload one. PNG, JPEG, WebP or GIF, up to 2 MB. Shown on your public status page.
          </small>
          <small *ngIf="logoError" class="text-danger d-block" data-testid="project-logo-error">{{ logoError }}</small>
        </div>
        <div *ngIf="logoDragOver" class="logo-field__drop-hint" aria-hidden="true">Drop to upload</div>
      </div>
    </div>

    <!-- Status page slug (edit only) -->
    <div *ngIf="isEditMode" class="form-group mb-4">
      <label for="statusPageSlug">Status Page URL slug</label>
      <div class="slug-input-group">
        <span class="slug-input-group__prefix">/status-page/</span>
        <input type="text" class="form-control" id="statusPageSlug"
          [(ngModel)]="statusPage.slug" [ngModelOptions]="{standalone: true}"
          (input)="statusPage.slug = statusPage.slug.toLowerCase().replace(' ', '-'); slugError = ''"
          placeholder="my-project">
      </div>
      <small *ngIf="slugError" class="text-danger">{{ slugError }}</small>
      <small class="text-muted">Must be unique across all projects.</small>
    </div>

    <!-- Timestamps (edit only) -->
    <div *ngIf="isEditMode" class="row mb-4">
      <div class="col">
        <label>Created</label>
        <p class="form-control-plaintext">{{ project.created_at | date:'medium' }}</p>
      </div>
      <div class="col">
        <label>Last Updated</label>
        <p class="form-control-plaintext">{{ project.updated_at | date:'medium' }}</p>
      </div>
    </div>

    <!-- Components -->
    <div class="mb-4">
      <label class="d-block mb-2">Components</label>

      <div *ngFor="let comp of components; let i = index" class="component-card mb-2">

        <!-- Header row: toggle + name + remove -->
        <div class="component-card__header">
          <button class="component-card__toggle" type="button" (click)="toggleComponent(i)"
            [title]="expandedComponents.has(i) ? 'Collapse' : 'Expand'">
            {{ expandedComponents.has(i) ? '▾' : '▸' }}
          </button>
          <input type="text" class="form-control component-card__name"
            [(ngModel)]="comp.name" [ngModelOptions]="{standalone: true}"
            (blur)="saveComponent(comp)"
            placeholder="Component name">
          <button class="pw-btn pw-btn--danger-outline component-card__remove"
            (click)="removeComponent(comp, i)" type="button">Remove</button>
        </div>

        <!-- Expanded detail panel -->
        <div class="component-card__body" *ngIf="expandedComponents.has(i)">
          <div class="row">
            <div class="col-12 mb-3">
              <label>Description</label>
              <textarea class="form-control" rows="2"
                [(ngModel)]="comp.description" [ngModelOptions]="{standalone: true}"
                (blur)="saveComponent(comp)"
                placeholder="Short description of this component"></textarea>
            </div>
          </div>
          <div class="row">
            <div class="col-md-2 mb-3">
              <label>Check Type</label>
              <select class="form-control"
                [(ngModel)]="comp.check_type" [ngModelOptions]="{standalone: true}"
                (change)="saveComponent(comp)">
                <option value="HTTP">HTTP</option>
              </select>
            </div>
            <div class="col-md-7 mb-3">
              <label>URL to check</label>
              <input type="url" class="form-control"
                [(ngModel)]="comp.check_url" [ngModelOptions]="{standalone: true}"
                (blur)="saveComponent(comp)"
                placeholder="https://example.com/api/health">
            </div>
            <div class="col-md-3 mb-3">
              <label>Check Interval</label>
              <select class="form-control"
                [(ngModel)]="comp.check_interval_seconds" [ngModelOptions]="{standalone: true}"
                (change)="saveComponent(comp)">
                <option [ngValue]="30">30 seconds</option>
                <option [ngValue]="60">1 minute</option>
                <option [ngValue]="300">5 minutes</option>
                <option [ngValue]="600">10 minutes</option>
                <option [ngValue]="1800">30 minutes</option>
                <option [ngValue]="3600">1 hour</option>
              </select>
            </div>
          </div>

          <!-- Maintenance windows (edit mode only) -->
          <div *ngIf="isEditMode && comp.id" class="maintenance-section">
            <button type="button" class="maintenance-toggle"
              (click)="toggleMaintenance(comp)">
              🔧 Maintenance windows
              <span class="maintenance-toggle__chevron">{{ showMaintenanceFor.has(comp.id) ? '▾' : '▸' }}</span>
            </button>

            <div *ngIf="showMaintenanceFor.has(comp.id)" class="maintenance-panel">

              <!-- Existing windows -->
              <div *ngIf="maintenanceWindows[comp.id]?.length > 0" class="maintenance-list">
                <div *ngFor="let w of maintenanceWindows[comp.id]" class="maintenance-item">
                  <div class="maintenance-item__info">
                    <span class="maintenance-item__title">{{ w.title || 'Maintenance' }}</span>
                    <span class="maintenance-item__range">
                      {{ w.start_at | date:'short' }} – {{ w.end_at | date:'short' }}
                    </span>
                    <span *ngIf="w.message" class="maintenance-item__msg">{{ w.message }}</span>
                  </div>
                  <button type="button" class="pw-btn pw-btn--danger-outline"
                    (click)="deleteMaintenanceWindow(comp, w.id)">Remove</button>
                </div>
              </div>
              <p *ngIf="maintenanceWindows[comp.id]?.length === 0" class="maintenance-empty">
                No maintenance windows scheduled.
              </p>

              <!-- Add new window -->
              <div class="maintenance-new" *ngIf="newWindow[comp.id]">
                <div class="row">
                  <div class="col-12 mb-2">
                    <label>Title <span class="text-muted">(optional)</span></label>
                    <input type="text" class="form-control"
                      [(ngModel)]="newWindow[comp.id].title" [ngModelOptions]="{standalone: true}"
                      placeholder="e.g. Database migration">
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-6 mb-2">
                    <label>Start</label>
                    <input type="datetime-local" class="form-control"
                      [(ngModel)]="newWindow[comp.id].start_at" [ngModelOptions]="{standalone: true}">
                  </div>
                  <div class="col-md-6 mb-2">
                    <label>End</label>
                    <input type="datetime-local" class="form-control"
                      [(ngModel)]="newWindow[comp.id].end_at" [ngModelOptions]="{standalone: true}">
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 mb-2">
                    <label>Message for visitors <span class="text-muted">(optional)</span></label>
                    <textarea class="form-control" rows="2"
                      [(ngModel)]="newWindow[comp.id].message" [ngModelOptions]="{standalone: true}"
                      placeholder="e.g. We are performing scheduled maintenance. Service will be restored shortly."></textarea>
                  </div>
                </div>
                <button type="button" class="pw-btn pw-btn--ghost"
                  (click)="addMaintenanceWindow(comp)">
                  + Schedule
                </button>
              </div>

            </div>
          </div>

        </div>

      </div>

      <!-- One window across several components. Only worth showing when there is
           more than one component to spread it over. -->
      <div *ngIf="isEditMode && components.length > 1" class="maintenance-section"
           data-testid="maintenance-fanout">
        <button type="button" class="maintenance-toggle" (click)="showFanout = !showFanout">
          🔧 Schedule maintenance across components
          <span class="maintenance-toggle__chevron">{{ showFanout ? '▾' : '▸' }}</span>
        </button>

        <div *ngIf="showFanout" class="maintenance-panel">
          <p class="maintenance-empty">
            Creates the same window on each component you pick, so the times cannot drift
            apart. Windows still belong to a component — the ones you leave unticked are
            unaffected.
          </p>

          <div class="row">
            <div class="col-12 mb-2">
              <label>Components</label>
              <div class="fanout-components">
                <label *ngFor="let comp of components" class="fanout-components__item">
                  <input type="checkbox" *ngIf="comp.id"
                    [checked]="fanoutComponentIds.has(comp.id)"
                    (change)="toggleFanoutComponent(comp)">
                  {{ comp.name }}
                </label>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-12 mb-2">
              <label>Title <span class="text-muted">(optional)</span></label>
              <input type="text" class="form-control"
                [(ngModel)]="fanoutWindow.title" [ngModelOptions]="{standalone: true}"
                placeholder="e.g. Database migration">
            </div>
          </div>
          <div class="row">
            <div class="col-md-6 mb-2">
              <label>Start</label>
              <input type="datetime-local" class="form-control"
                [(ngModel)]="fanoutWindow.start_at" [ngModelOptions]="{standalone: true}">
            </div>
            <div class="col-md-6 mb-2">
              <label>End</label>
              <input type="datetime-local" class="form-control"
                [(ngModel)]="fanoutWindow.end_at" [ngModelOptions]="{standalone: true}">
            </div>
          </div>
          <div class="row">
            <div class="col-12 mb-2">
              <label>Message for visitors <span class="text-muted">(optional)</span></label>
              <textarea class="form-control" rows="2"
                [(ngModel)]="fanoutWindow.message" [ngModelOptions]="{standalone: true}"
                placeholder="e.g. We are performing scheduled maintenance. Service will be restored shortly."></textarea>
            </div>
          </div>

          <button type="button" class="pw-btn pw-btn--ghost"
            [disabled]="fanoutComponentIds.size === 0"
            (click)="addMaintenanceWindowForComponents()">
            + Schedule on {{ fanoutComponentIds.size }}
            {{ fanoutComponentIds.size === 1 ? 'component' : 'components' }}
          </button>
        </div>
      </div>

      <div *ngIf="components.length === 0" class="text-muted mb-2" style="font-size:14px">
        No components yet.
      </div>

      <!-- Add new component -->
      <div class="d-flex gap-2 mt-2">
        <input type="text" class="form-control" placeholder="New component name"
          [(ngModel)]="newComponentName" [ngModelOptions]="{standalone: true}"
          (keyup.enter)="addComponent()">
        <button class="pw-btn pw-btn--ghost" (click)="addComponent()" type="button">
          + Add
        </button>
      </div>
    </div>
  </form>

  <div class="d-flex gap-2 mt-4">
    <button class="pw-btn pw-btn--primary" (click)="onSave()">
      {{ isEditMode ? 'Save Changes' : 'Create Project' }}
    </button>
    <button class="pw-btn pw-btn--ghost" routerLink="/main/dashboard">Cancel</button>
  </div>

</div>

<!-- Delete confirmation modal -->
<div class="modal-backdrop" *ngIf="showDeleteModal" (click)="showDeleteModal = false">
  <div class="modal-box" (click)="$event.stopPropagation()">
    <h3 class="modal-box__title">Delete Project</h3>
    <p class="modal-box__body">
      Are you sure you want to delete <strong>{{ project.name }}</strong>?
      This action cannot be undone.
    </p>
    <div class="modal-box__actions">
      <button class="pw-btn pw-btn--danger" (click)="onDelete()">Yes, delete</button>
      <button class="pw-btn pw-btn--ghost" (click)="showDeleteModal = false">Cancel</button>
    </div>
  </div>
</div>
